import { useEffect, useRef, useState } from "react";
import PortalProjects from "./components/PortalProjects";
import { getSession } from "../../utils/authSession";
import { getUser } from "../../api/LoginApi";
import {
    Briefcase,
    Mail,
    Shield,
    ArrowRight,
    Users,
    FileText,
    CreditCard,
    Activity,
    Settings,
    Edit2,
    Trash2
} from "react-feather";
import "./Portal.css";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// const PORTAL_AUTH_KEY = "ai4signs_portal_auth";

function LoadingSpinner({ small = false }) {
    return <span className={`portal-spinner ${small ? "small" : ""}`} />;
}

export default function Portal({
    onOpenRevision
}) {
    const [companyName, setCompanyName] = useState("");

    const loginSession = getSession();
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");

    const [step, setStep] = useState("email");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [portalSession, setPortalSession] = useState(null);

    const [users, setUsers] = useState([]);
    const [addUserOpen, setAddUserOpen] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState("");
    const [newUserRole, setNewUserRole] = useState("member");

    const [portalMode, setPortalMode] = useState(null);

    const [resendCountdown, setResendCountdown] = useState(0);
    const [resendMessage, setResendMessage] = useState("");

    const [clientsOpen, setClientsOpen] = useState(false);
    const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
    const [editingCompanyName, setEditingCompanyName] = useState(false);
    const [updatedCompanyName, setUpdatedCompanyName] = useState("");

    const [userSearch, setUserSearch] = useState("");
    const [clientSearch, setClientSearch] = useState("");

    const [projectCount, setProjectCount] = useState(0);

    const codeRef = useRef(null);

    useEffect(() => {

        async function initializePortal() {

            if (!loginSession?.email) return;

            try {

                setLoading(true);

                const user = await getUser(
                    loginSession.userId,
                    loginSession.email
                );

                // Must be Owner or Admin
                if (
                    user.role !== "owner" &&
                    user.role !== "admin"
                ) {
                    setMessage("You don't have permission to access the Company Portal.");
                    return;
                }

                // Existing company
                if (user.companyId) {

                    const session = {
                        companyId: user.companyId,
                        companyName: user.companyName,
                        email: user.email,
                        role: user.role,
                        status: user.status
                    };

                    setPortalSession(session);
                    setCompanyName(user.companyName || "");
                    setEmail(user.email);
                    setStep("dashboard");

                    await loadCompanyUsers(session);

                    return;
                }

                // New company
                setEmail(user.email);
                setStep("company");

            } catch (err) {

                setMessage(err.message);

            } finally {

                setLoading(false);

            }

        }

        initializePortal();

    }, []);

    const emailValid =
        email.includes("@") &&
        email.includes(".") &&
        email.trim().length > 5;

    const companyValid = companyName.trim().length >= 2;
    const canContinueEmail = emailValid && !loading;
    const canContinueCompany = emailValid && companyValid && !loading;

    async function handleCheckPortalEmail() {
        if (!canContinueEmail) return;

        try {
            setLoading(true);
            setMessage("");

            const res = await fetch(`${API_BASE_URL}portal/check-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: email.toLowerCase().trim()
                })
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Unable to verify this email.");
            }

            if (data.mode === "existing") {
                setPortalMode("existing");
                setCompanyName(data.company.name);

                await handleSendCode({
                    forcedCompanyName: data.company.name
                });

                return;
            }

            setPortalMode("new");
            setStep("company");

        } catch (err) {
            setMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleSendCode(options = {}) {
        const finalCompanyName =
            options.forcedCompanyName || companyName.trim();

        if (!emailValid || !finalCompanyName || loading) return;

        try {
            setLoading(true);
            setMessage("");

            const res = await fetch(`${API_BASE_URL}portal-send-access-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    companyName: finalCompanyName,
                    email: email.toLowerCase().trim()
                })
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Access not allowed.");
            }

            setCompanyName(finalCompanyName);
            setStep("code");

            setTimeout(() => {
                codeRef.current?.focus();
            }, 250);
        } catch (err) {
            setMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleUpdateCompanyName() {
        const cleanName = updatedCompanyName.trim();

        if (cleanName.length < 2) return;

        try {
            setLoading(true);
            setMessage("");

            const res = await fetch(`${API_BASE_URL}portal/update-company-name`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    companyId: portalSession.companyId,
                    requesterEmail: portalSession.email,
                    companyName: cleanName
                })
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Failed to update company name.");
            }

            const updatedSession = {
                ...portalSession,
                companyName: data.company.name
            };

            // localStorage.setItem(PORTAL_AUTH_KEY, JSON.stringify(updatedSession));

            setPortalSession(updatedSession);
            setCompanyName(data.company.name);
            setEditingCompanyName(false);
        } catch (err) {
            showPortalMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleConfirmCode() {
        if (code.length !== 6 || loading) return;

        try {
            setLoading(true);
            setMessage("");

            const res = await fetch(`${API_BASE_URL}portal-check-access-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    companyName: companyName.trim(),
                    email: email.toLowerCase().trim(),
                    accessCode: code
                })
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Invalid or expired code.");
            }

            const session = {
                companyId: data.company.id,
                companyName: data.company.name,
                email: data.user.email,
                role: data.user.role,
                status: data.user.status,
                expiresAt: Date.now() + 7 * 24 * 60 * 60 * 1000
            };

            // localStorage.setItem(PORTAL_AUTH_KEY, JSON.stringify(session));

            setPortalSession(session);

            setStep("dashboard");
            await loadCompanyUsers(session);
        } catch (err) {
            setMessage(err.message);
            setCode("");
            codeRef.current?.focus();
        } finally {
            setLoading(false);
        }
    }

    function handlePortalLogout() {
        // localStorage.removeItem(PORTAL_AUTH_KEY);

        setPortalSession(null);

        setUsers([]);

        setEmail("");
        setCompanyName("");
        setCode("");

        setNewUserEmail("");
        setNewUserRole("member");
        setAddUserOpen(false);

        setMessage("");

        setStep("email");
    }

    async function loadCompanyUsers(session = portalSession) {
        if (!session?.companyId) return;

        try {
            const res = await fetch(
                `${API_BASE_URL}portal/users?companyId=${session.companyId}`
            );

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Failed to load users.");
            }

            setUsers(data.users || []);
        } catch (err) {
            setMessage(err.message);
        }
    }

    async function handleAddUser() {
        const cleanEmail = newUserEmail.toLowerCase().trim();

        if (!cleanEmail.includes("@") || !cleanEmail.includes(".")) return;

        try {
            setLoading(true);

            const res = await fetch(`${API_BASE_URL}portal/add-user`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    companyId: portalSession.companyId,
                    requesterEmail: portalSession.email,
                    email: cleanEmail,
                    role: newUserRole
                })
            });

            const data = await res.json();

            if (res.status === 409) {
                throw new Error("This email is already connected to this company.");
            }

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Failed to add user.");
            }

            await loadCompanyUsers();

            setNewUserEmail("");
            setNewUserRole("member");
            setAddUserOpen(false);

        } catch (err) {
            setMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    function showPortalMessage(message) {
        setMessage(message);

        setTimeout(() => {
            setMessage("");
        }, 3200);
    }

    async function handleRoleChange(user, role) {
        try {
            setLoading(true);
            setMessage("");

            const res = await fetch(`${API_BASE_URL}portal/update-user-role`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    companyId: portalSession.companyId,
                    requesterEmail: portalSession.email,
                    userId: user.id,
                    role
                })
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Failed to update role.");
            }

            setUsers((prev) =>
                prev.map((u) =>
                    u.id === user.id ? { ...u, role: data.user.role } : u
                )
            );
        } catch (err) {
            showPortalMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleToggleUser(user) {
        const endpoint =
            user.status === "active"
                ? "portal/remove-user"
                : "portal/activate-user";

        try {
            setLoading(true);
            setMessage("");

            const res = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    companyId: portalSession.companyId,
                    requesterEmail: portalSession.email,
                    userId: user.id
                })
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Failed to update user.");
            }

            setUsers((prev) =>
                prev.map((u) =>
                    u.id === user.id
                        ? {
                            ...u,
                            status:
                                user.status === "active"
                                    ? "deactivated"
                                    : "active"
                        }
                        : u
                )
            );
        } catch (err) {
            showPortalMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleResendCode() {
        if (loading || resendCountdown > 0) return;

        await handleSendCode();

        setCode("");
        setMessage("");
        setResendMessage("New code has just been sent!");

        let count = 10;
        setResendCountdown(count);

        const timer = setInterval(() => {
            count -= 1;
            setResendCountdown(count);

            if (count <= 0) {
                clearInterval(timer);
                setResendMessage("");
            }
        }, 1000);
    }

    async function handleDeleteUser(user) {
        if (!user?.id) return;

        try {
            setLoading(true);
            setMessage("");

            const res = await fetch(`${API_BASE_URL}portal/delete-user`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    companyId: portalSession.companyId,
                    requesterEmail: portalSession.email,
                    userId: user.id
                })
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.error || "Failed to delete user.");
            }

            setUsers((prev) => prev.filter((u) => u.id !== user.id));
            setConfirmDeleteUser(null);
        } catch (err) {
            showPortalMessage(err.message);
        } finally {
            setLoading(false);
        }
    }

    const companyUsers = users
        .filter((user) => user.role !== "client")
        .filter((user) =>
            user.email?.toLowerCase().includes(userSearch.toLowerCase().trim())
        );

    const companyClients = users
        .filter((user) => user.role === "client")
        .filter((user) =>
            user.email?.toLowerCase().includes(clientSearch.toLowerCase().trim())
        );

    if (step === "dashboard") {
        return (
            <main className="portal-page">
                <section className="portal-dashboard">
                    <div className="portal-dashboard-header">
                        <div>
                            <div className="portal-eyebrow">Company Workspace</div>
                            <div className="portal-company-title-row">
                                {!editingCompanyName ? (
                                    <>
                                        <h1>{portalSession?.companyName || companyName}</h1>

                                        <button
                                            className="portal-icon-btn"
                                            onClick={() => {
                                                setUpdatedCompanyName(portalSession?.companyName || companyName);
                                                setEditingCompanyName(true);
                                            }}
                                            title="Edit company name"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                    </>
                                ) : (
                                    <div className="portal-company-edit">
                                        <input
                                            value={updatedCompanyName}
                                            onChange={(e) => setUpdatedCompanyName(e.target.value)}
                                            autoFocus
                                        />

                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                gap: "10px",
                                                alignItems: "center"
                                            }}
                                        >
                                            <button
                                                className="portal-mini-btn"
                                                onClick={handleUpdateCompanyName}
                                                disabled={loading}
                                            >
                                                Save
                                            </button>

                                            <button
                                                className="portal-secondary small"
                                                onClick={() => setEditingCompanyName(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <p>{portalSession?.email || email}</p>
                        </div>
                    </div>

                    <div className="portal-stats-grid">
                        <div className="portal-stat-card">
                            <Users size={22} />
                            <strong>{companyUsers.length}</strong>
                            <span>Users</span>
                        </div>

                        <div className="portal-stat-card">
                            <Briefcase size={22} />
                            <strong>{companyClients.length}</strong>
                            <span>Clients</span>
                        </div>

                        <div className="portal-stat-card">
                            <FileText size={22} />
                            <strong>{projectCount}</strong>
                            <span>Projects</span>
                        </div>

                        <div className="portal-stat-card">
                            <CreditCard size={22} />
                            <strong>Trial</strong>
                            <span>Plan</span>
                        </div>
                    </div>

                    <div className="portal-dashboard-grid">
                        <div className="portal-panel">
                            <div className="portal-panel-title portal-panel-title-between">
                                <div>
                                    <Users size={18} />
                                    <h3>Company Users</h3>
                                </div>

                                <div className="portal-panel-actions">
                                    <button
                                        className="portal-mini-btn"
                                        style={{ backgroundColor: "#111" }}
                                        onClick={() => setClientsOpen(true)}
                                    >
                                        Clients
                                    </button>

                                    <button
                                        className="portal-mini-btn"
                                        onClick={() => setAddUserOpen(true)}
                                    >
                                        Add User
                                    </button>
                                </div>
                            </div>

                            <input
                                className="portal-search-input"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                placeholder="Search users..."
                            />

                            <div className="portal-users-list">
                                {companyUsers.map((user) => (
                                    <div className="portal-user-row" key={user.id || user.email}>
                                        <div className="portal-user-info">
                                            <strong>{user.email}</strong>

                                            <span>
                                                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                            </span>
                                        </div>

                                        <div className="portal-user-actions">

                                            <select
                                                className="portal-role-select"
                                                value={user.role}
                                                disabled={
                                                    user.email === portalSession.email &&
                                                    user.role === "owner"
                                                }
                                                onChange={(e) =>
                                                    handleRoleChange(user, e.target.value)
                                                }
                                            >
                                                <option value="member">Member</option>
                                                <option value="admin">Admin</option>
                                                <option value="client">Client</option>
                                                <option value="owner">Owner</option>
                                            </select>

                                            <label className="portal-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={user.status === "active"}
                                                    disabled={
                                                        user.email === portalSession.email &&
                                                        user.role === "owner"
                                                    }
                                                    onChange={() => handleToggleUser(user)}
                                                />

                                                <span className="portal-slider"></span>
                                            </label>

                                            <button
                                                className="portal-delete-icon-btn"
                                                disabled={user.email === portalSession.email && user.role === "owner"}
                                                onClick={() => setConfirmDeleteUser(user)}
                                                title="Delete user"
                                            >
                                                <Trash2 size={16} />
                                            </button>

                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <PortalProjects
                            portalSession={portalSession}
                            onProjectsLoaded={(projects) => {
                                setProjectCount(projects.length);
                            }}
                            onOpenRevision={onOpenRevision}
                        />

                        <div className="portal-panel">
                            <div className="portal-panel-title">
                                <CreditCard size={18} />
                                <h3>Billing</h3>
                            </div>

                            <div className="portal-info-list">
                                <div><span>Status</span><strong>Trial</strong></div>
                                <div><span>Invoices</span><strong>0</strong></div>
                                <div><span>Payment method</span><strong>Not connected</strong></div>
                            </div>
                        </div>

                        <div className="portal-panel">
                            <div className="portal-panel-title">
                                <Settings size={18} />
                                <h3>Company Settings</h3>
                            </div>

                            <div className="portal-info-list">
                                <div><span>Company ID</span><strong>{portalSession?.companyId || "Pending"}</strong></div>
                                <div><span>Role</span><strong>{portalSession?.role?.charAt(0).toUpperCase() + portalSession?.role?.slice(1) || "Owner"}</strong></div>
                                <div><span>Workspace</span><strong>Active</strong></div>
                            </div>
                        </div>
                    </div>
                </section>

                {message && (
                    <div className="portal-message-backdrop" onClick={() => setMessage("")}>
                        <div
                            className="portal-message-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3>Action not allowed</h3>
                            <p>{message}</p>

                            <button
                                className="portal-primary"
                                onClick={() => setMessage("")}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}

                {addUserOpen && (
                    <div
                        className="portal-user-modal-backdrop"
                        onClick={() => setAddUserOpen(false)}
                    >
                        <div
                            className="portal-user-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3>Add User</h3>
                            <p>Invite a user to this company workspace.</p>

                            <div className="portal-field">
                                <label>
                                    <Mail size={15} />
                                    User email
                                </label>

                                <input
                                    type="email"
                                    value={newUserEmail}
                                    onChange={(e) => setNewUserEmail(e.target.value)}
                                    placeholder="user@company.com"
                                />
                            </div>

                            <div className="portal-field">
                                <label>Group</label>

                                <select
                                    className="portal-role-select full"
                                    value={newUserRole}
                                    onChange={(e) => setNewUserRole(e.target.value)}
                                >
                                    <option value="admin">Admin</option>
                                    <option value="client">Client</option>
                                    {/* <option value="designer">Designer</option>
                                    <option value="production">Production</option>
                                    <option value="sales">Sales Team</option>
                                    <option value="services">Services</option>
                                    <option value="viewer">Viewer</option> */}
                                    <option value="member">Member</option>
                                </select>
                            </div>

                            <div className="portal-modal-actions">
                                <button
                                    className="portal-secondary"
                                    onClick={() => setAddUserOpen(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="portal-primary"
                                    onClick={handleAddUser}
                                    disabled={loading}
                                >
                                    {loading ? <LoadingSpinner small /> : "Add user"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {clientsOpen && (
                    <div
                        className="portal-user-modal-backdrop"
                        onClick={() => setClientsOpen(false)}
                    >
                        <div
                            className="portal-user-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3>Clients</h3>
                            <p>Client users connected to this company.</p>

                            <div className="portal-users-list">
                                {companyClients.length === 0 && (
                                    <div className="portal-empty-box">Any client has been found or created.</div>
                                )}

                                <input
                                    className="portal-search-input"
                                    value={clientSearch}
                                    onChange={(e) => setClientSearch(e.target.value)}
                                    placeholder="Search clients..."
                                />

                                {companyClients.map((client) => (
                                    <div className="portal-user-row" key={client.id || client.email}>
                                        <div className="portal-user-info">
                                            <strong>{client.email}</strong>
                                            <span>Client</span>
                                        </div>

                                        <button
                                            className="portal-delete-icon-btn"
                                            onClick={() => setConfirmDeleteUser(client)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="portal-modal-actions">
                                <button
                                    className="portal-secondary"
                                    onClick={() => setClientsOpen(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {confirmDeleteUser && (
                    <div className="portal-message-backdrop" onClick={() => setConfirmDeleteUser(null)}>
                        <div
                            className="portal-message-modal"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3>Delete user?</h3>
                            <p>
                                Are you sure you want to delete <strong>{confirmDeleteUser.email}</strong>?
                            </p>

                            <button
                                className="portal-secondary"
                                onClick={() => setConfirmDeleteUser(null)}
                            >
                                Cancel
                            </button>

                            <button
                                className="portal-primary" style={{ marginTop: "20px" }}
                                onClick={() => handleDeleteUser(confirmDeleteUser)}
                                disabled={loading}
                            >
                                {loading ? <LoadingSpinner small /> : "Delete"}
                            </button>
                        </div>
                    </div>
                )}
            </main>
        );
    }

    return (
        <main className="portal-page">
            <section className="portal-card">
                <div className="portal-brand">
                    <div className="portal-logo">
                        <Briefcase size={24} />
                    </div>

                    <div>
                        <h1>Company Portal</h1>
                        <p>Create or access your AI 4 Signs company workspace.</p>
                    </div>
                </div>


                {/* {step === "email" && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleCheckPortalEmail();
                        }}
                    >
                        <div className="portal-field">
                            <label>
                                <Mail size={15} />
                                Admin email
                            </label>

                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@company.com"
                                disabled={loading}
                            />
                        </div>

                        <div className="portal-note">
                            Enter your registered business email to access your company workspace.
                        </div>

                        {message && <div className="portal-error">{message}</div>}

                        <button
                            className={`portal-primary ${canContinueEmail ? "" : "disabled"}`}
                            disabled={!canContinueEmail}
                            onClick={handleCheckPortalEmail}
                        >
                            {loading ? <LoadingSpinner small /> : "Next"}
                            {!loading && <ArrowRight size={16} />}
                        </button>
                    </form>
                )} */}

                {step === "company" && (
                    <>
                        <div className="portal-field">
                            <label>
                                <Briefcase size={15} />
                                Company name
                            </label>

                            <input
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="Your company name"
                                disabled={loading}
                            />
                        </div>

                        <div className="portal-note">
                            This email is approved to create a company workspace. Please enter your company name to continue.
                        </div>

                        {message && <div className="portal-error">{message}</div>}

                        <button
                            className={`portal-primary ${canContinueCompany ? "" : "disabled"}`}
                            disabled={!canContinueCompany}
                            onClick={() => handleSendCode()}
                        >
                            {loading ? <LoadingSpinner small /> : "Send access code"}
                            {!loading && <ArrowRight size={16} />}
                        </button>

                        <button
                            className="portal-secondary"
                            onClick={() => {
                                setStep("email");
                                setCompanyName("");
                                setMessage("");
                            }}
                        >
                            Use another email
                        </button>
                    </>
                )}

                {step === "code" && (
                    <>
                        <div className="portal-code-header">
                            <div className="portal-code-icon">
                                <Shield size={24} />
                            </div>

                            <h2>Verify your email</h2>
                            <p>
                                We sent a 6-digit code to <strong>{email}</strong>.
                            </p>
                        </div>

                        <input
                            ref={codeRef}
                            className="portal-code-input"
                            inputMode="numeric"
                            maxLength={6}
                            value={code}
                            onChange={(e) =>
                                setCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))
                            }
                            placeholder="123456"
                            disabled={loading}
                        />

                        {message && <div className="portal-error">{message}</div>}

                        <button
                            className={`portal-primary ${code.length === 6 ? "" : "disabled"}`}
                            disabled={code.length !== 6 || loading}
                            onClick={handleConfirmCode}
                        >
                            {loading ? <LoadingSpinner small /> : "Confirm access"}
                            {!loading && <ArrowRight size={16} />}
                        </button>

                        <button
                            className="portal-secondary"
                            onClick={() => {
                                setStep("email");
                                setCode("");
                                setMessage("");
                            }}
                        >
                            Use another email
                        </button>

                        <button
                            className="portal-link-btn"
                            onClick={handleResendCode}
                            disabled={loading || resendCountdown > 0}
                        >
                            {resendMessage || "Resend code"}
                            {resendCountdown > 0 && ` (${resendCountdown})`}
                        </button>

                    </>
                )}


            </section>
        </main>
    );
}